// Composer Studio — 신규 문서 starter(빈 양식) + 미리보기 placeholder RenderData.
//
// emptyTemplate: 새 문서 생성/초기 마운트/삭제 후 캔버스 = 완전히 빈 양식.
// placeholderData: 미리보기에서 미해석 binding의 fallback 표시값(데모).

import type { ReportTemplate, RenderData } from '@safetyhub/report-composer';

// ── Composer 단위 정책(편집 UI ↔ 저장) ──────────────────────────────
// 저장·엔진은 전부 px(A4=794px, 노드 layout/margins/fontSize 모두 px). 단위 일관성 유지.
// 편집 UI만 인쇄 문서 관습으로 환산해 표시:
//   · 물리 치수·간격(페이지 여백·width·minHeight·결재칸·열 너비·행 높이·패딩) → mm (mmToPx/pxToMm)
//       (mm 2자리(0.01mm)로 1px=0.26mm 같은 작은 값도 표현 가능 → 패딩도 mm로 통일)
//   · 글꼴 크기 → pt (ptToPx/pxToPt)
//   · 테두리(border)는 CSS 문자열('1px solid #000')이라 그 안의 px는 그대로 둠(숫자 변환 대상 아님)
//
// ── 페이지 여백 단위 ───────────────────────────────────────────────
// Composer 엔진은 전부 px(A4=794px, 노드 layout px, page.margins도 px padding).
// 단위 일관성을 위해 margins도 px로 저장하고, 편집 UI에서만 한글식 mm로 표시(변환).
// 소수 입력 지원 — 표시는 mm 2자리(HWP와 동일, 0.01mm) / pt 1자리(0.1pt),
// 저장(px)은 표시 정밀도를 깨끗이 왕복할 수 있도록 더 촘촘하게: mm는 px 3자리, pt는 px 2자리.
// (px를 정수로 반올림하면 기본 10mm가 10.05mm로 되돌아오는 드리프트가 생김 → px를 촘촘히 보존.)
/** 96dpi 기준 1mm = 3.779528px */
export const MM_TO_PX = 96 / 25.4;
export const mmToPx = (mm: number): number => Math.round(mm * MM_TO_PX * 1000) / 1000;
export const pxToMm = (px: number): number => Math.round((px / MM_TO_PX) * 100) / 100;

/**
 * px 임계값을 **「이 mm를 입력하면 그 임계를 넘는다」가 참인** mm로 환산한다(2자리, 올림).
 *
 * ⚠️ **임계값 안내에 `pxToMm`을 쓰면 안내가 거짓이 된다** — 그것은 *가장 가까운* 값을 주므로
 * 내려갈 수 있고, 판정은 px에서 하기 때문이다. 2026-09-30 dev 실측이 그 결함을 잡았다:
 * 페이지번호 겹침 경고가 「하단 여백을 **4.76mm 이상**으로 두세요」라 안내했는데
 * `pxToMm(18) = 4.76` → `mmToPx(4.76) = 17.99px < 18`이라 **안내대로 입력해도 경고가 그대로**였다
 * (4.77에서 사라진다). 사용자는 「값을 넣었는데 왜 안 없어지나」를 겪는다.
 *
 * 현재값 «표시»에는 `pxToMm`이 옳다(가장 가까운 값). 이 함수는 **입력하라고 제시하는 값** 전용이다 —
 * 그 둘은 목적이 다르므로 한 함수로 합치지 말 것. (사용처는 `EditorPage`의 겹침 경고 한 곳이고,
 * 나머지 `pxToMm` 15곳은 전부 현재값 표시다 — 2026-09-30 전수 확인.)
 */
export const pxToMmAtLeast = (px: number): number => {
  const mm = Math.ceil((px / MM_TO_PX) * 100) / 100;
  // 올림이 부동소수 오차로 모자라면 한 칸 더 — 왕복을 «보장»하는 것이 이 함수의 계약이다.
  // ⚠️ 이 한 줄은 **올림과 독립인 2차 방어**다(2026-09-30 프로브로 확인): 올림을 반올림으로
  // 되돌려도 이것이 계약을 지켜 spec이 **무반응**이었고, 둘을 함께 걷어야 red가 됐다. 즉 spec은
  // 「올림을 쓴다」가 아니라 **「왕복이 보장된다」**를 잰다 — 그쪽이 옳은 계약이니 이 줄을
  // 「올림이 있으니 중복」이라며 지우지 말 것.
  return mmToPx(mm) >= px ? mm : Math.round((mm + 0.01) * 100) / 100;
};

// ── 글꼴 크기 단위 ─────────────────────────────────────────────────
// 엔진은 fontSize를 px로 렌더(primitives/셀). 인쇄 문서 관습대로 편집 UI는 pt로 표시(변환).
// 10.5pt 같은 소수 폰트(한글 본문 관습)도 지원.
/** 96dpi 기준 1pt = 1.333333px */
export const PT_TO_PX = 96 / 72;
export const ptToPx = (pt: number): number => Math.round(pt * PT_TO_PX * 100) / 100;
export const pxToPt = (px: number): number => Math.round((px / PT_TO_PX) * 10) / 10;

/** A4 기본 여백(mm)의 정본: 위 10 / 아래 15 / 좌·우 10. */
export const DEFAULT_MARGINS_MM = { top: 10, right: 10, bottom: 15, left: 10 } as const;
/** 위 mm 기본값을 px로 환산한 page.margins(저장 단위). */
export const defaultMarginsPx = (): ReportTemplate['page']['margins'] => ({
  top: mmToPx(DEFAULT_MARGINS_MM.top),
  right: mmToPx(DEFAULT_MARGINS_MM.right),
  bottom: mmToPx(DEFAULT_MARGINS_MM.bottom),
  left: mmToPx(DEFAULT_MARGINS_MM.left),
});

// 용지 크기 → mm 치수 표시는 report-composer의 pageMm(size+orientation, 단일 소스)로 이관.

/** 신규 문서 starter — 완전히 빈 양식(본문/작성폼/결재 없음). key/name은 생성 시 주입. */
export const emptyTemplate: ReportTemplate = {
  key: 'untitled',
  name: 'untitled',
  schemaVersion: 1,
  systemType: 'general',
  page: {
    size: 'A4',
    margins: defaultMarginsPx(), // 기존 보고서 통일 여백(위10/아래15/좌우10mm)
  },
  body: [],
};

/** 샘플 placeholder 데이터 (bindingKey → 표시 문자열) */
export const placeholderData: RenderData = {
  siteName: '○○ 제조 1공장',
  inspectionDate: '2026-06-19',
  inspectorName: '홍길동 안전관리자',
};
